import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { FiPlus, FiTrash2, FiCheckCircle, FiSearch, FiFilter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../contexts/AuthContext';
import TaskCard from './TaskCard';
import TaskModal from './TaskModal';
import TrashTab from './TrashTab';
import InReviewTab from './InReviewTab';
import FullTaskModal from './FullTaskModal';

import '../styles/KanbanBoard.css';


// ================= NORMALIZE =================

const normalize = (value) =>
  String(value || "").toLowerCase().trim();


// ================= PRIORITY VALUE =================

const getPriorityValue = (urgency) => {

  return Number(urgency) || 0;

};


//  AUTO SORT FUNCTION 

const autoSortTasks = (tasks = []) => {

  return [...tasks].sort((a, b) => {

    // 1. PRIORITY SORT
    const priorityDiff =
      getPriorityValue(b.urgency)
      -
      getPriorityValue(a.urgency);

    if (priorityDiff !== 0)
      return priorityDiff;


    // 2. DUE DATE SORT
    const dueA = a.dueDate
      ? new Date(a.dueDate).getTime()
      : Number.MAX_SAFE_INTEGER;

    const dueB = b.dueDate
      ? new Date(b.dueDate).getTime()
      : Number.MAX_SAFE_INTEGER;

    if (dueA !== dueB)
      return dueA - dueB;


    // 3. CREATED DATE SORT (NEWEST FIRST)
    const createdA = a.createdAt
      ? new Date(a.createdAt).getTime()
      : 0;

    const createdB = b.createdAt
      ? new Date(b.createdAt).getTime()
      : 0;

    return createdB - createdA;

  });

};


//  DROPPABLE COLUMN 

const DroppableColumn = ({ column, children, taskCount }) => {

  const { setNodeRef, isOver } =
    useDroppable({
      id: column.id
    });

  return (

    <div className="kanban-column">

      <div
        className="column-header"
        style={{ borderTopColor: column.color }}
      >
        <h2>{column.title}</h2>

        <span className="task-count">
          {taskCount}
        </span>

      </div>

      <div
        ref={setNodeRef}
        className={`column-content ${isOver ? "dragging-over" : ""
          }`}
      >
        {children}
      </div>

    </div>

  );
};


// ================= DROPPABLE BUTTON =================

const DroppableButton = ({ id, children, className, onClick, title }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <button
      ref={setNodeRef}
      id={id}
      className={`${className} ${isOver ? 'dragging-over-btn' : ''}`}
      onClick={onClick}
      title={title}
      style={isOver ? { transform: 'scale(1.1)', filter: 'contrast(1.3)' } : {}}
    >
      {children}
    </button>
  );
};

// ================= MAIN COMPONENT =================

const KanbanBoard = () => {

  const { tasks, projects, updateTaskStatus, deleteTask, loading } =
    useTask();

  const { user } = useAuth();

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [selectedTask, setSelectedTask] =
    useState(null);

  const [isFullModalOpen, setIsFullModalOpen] = useState(false);
  const [selectedTaskFull, setSelectedTaskFull] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  const [activeId, setActiveId] =
    useState(null);

  const [showTrash, setShowTrash] = useState(false);
  const [showInReview, setShowInReview] = useState(false);
  const isAdminOrManager = ['admin', 'manager'].includes(user?.role);


  //  DRAG 

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor)
  );


  //  COLUMNS 

  const columns = [
    { id: "todo", title: "To Do", color: "#6c757d" },
    { id: "in-progress", title: "In Progress", color: "#0d6efd" },
    { id: "done", title: "Done", color: "#198754" }
  ];


  // ================= SORT TASKS BY COLUMN =================

  const sortedTasks = useMemo(() => {

    if (!tasks || tasks.length === 0)
      return [];

    let filtered = tasks;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        (t.title || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.assignees || []).some(a => (a.username || a).toLowerCase().includes(q))
      );
    }

    if (priorityFilter) {
      filtered = filtered.filter(t => String(t.urgency) === String(priorityFilter));
    }

    if (projectFilter) {
      filtered = filtered.filter(t => {
        const tProjectId = t.projectId?._id || t.projectId;
        return String(tProjectId) === String(projectFilter);
      });
    }

    return autoSortTasks(filtered);

  }, [tasks, searchQuery, priorityFilter, projectFilter]);



  const getSortedColumnTasks = (status) => {

    return sortedTasks.filter(

      task =>
        normalize(task.status)
        === normalize(status)

    );

  };



  //   DRAG HANDLERS  

  const handleDragStart = (event) => {

    setActiveId(event.active.id);

  };


  const handleDragEnd = (event) => {

    const { active, over } = event;

    setActiveId(null);

    if (!over) return;

    // HR can view but not modify tasks
    if (user?.role === 'hr') return;

    const activeTask =
      tasks.find(t => t._id === active.id);

    if (!activeTask) return;

    let newStatus = null;

    if (
      columns.some(c => c.id === over.id)
    )
      newStatus = over.id;

    else if (over.id === 'trash') {
      newStatus = 'trash';
    }
    else if (over.id === 'review') {
      newStatus = 'review';
    }
    else {
      const overTask = tasks.find(t => t._id === over.id);
      if (overTask) newStatus = overTask.status;
    }

    if (newStatus === 'trash') {
      deleteTask(active.id);
      return;
    }

    if (newStatus === 'review') {
      if (!activeTask.reviewerId) {
        toast.error("A manager must be assigned before moving to Review.");
        return;
      }
      updateTaskStatus(
        active.id,
        "review"
      );
      toast.success("Task sent to review");
      return;
    }

    if (
      newStatus &&
      newStatus !== activeTask.status
    ) {
      if (!activeTask.reviewerId && newStatus !== 'todo') {
        toast.error("A manager must be assigned before moving out of To Do.");
        return;
      }

      updateTaskStatus(
        active.id,
        newStatus
      );

      toast.success("Task moved");

    }

  };


  // ================= MODAL =================

  const openCreateModal = () => {

    setSelectedTask(null);

    setIsModalOpen(true);

  };


  const openEditModal = (task) => {

    setSelectedTask(task);

    setIsModalOpen(true);

  };


  const openFullModal = (task) => {
    setSelectedTaskFull(task);
    setIsFullModalOpen(true);
  };


  const closeFullModal = () => {
    setSelectedTaskFull(null);
    setIsFullModalOpen(false);
  };


  const closeModal = () => {

    setSelectedTask(null);

    setIsModalOpen(false);

  };


  // ================= LOADING =================

  if (loading)
    return <div>Loading tasks...</div>;


  // ================= UI =================

  return (

    <div className="kanban-container">

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >

        <div className="kanban-header">

          <h1>Task Board</h1>

          {user?.role !== 'hr' && (
            <div className="header-actions">

              <div className="search-box">
                <FiSearch size={16} />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ border: 'none', outline: 'none', background: 'transparent' }}
                />
              </div>

              <div className="urgency-filter">
                <FiFilter size={16} color="var(--text-light)" />
                <select
                  className="filter-select"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="">All Priorities</option>
                  <option value="5">Priority 5 (Highest)</option>
                  <option value="4">Priority 4</option>
                  <option value="3">Priority 3</option>
                  <option value="2">Priority 2</option>
                  <option value="1">Priority 1 (Lowest)</option>
                </select>
              </div>

              {projects && projects.length > 0 && (
                <div className="urgency-filter">
                  <FiFilter size={16} color="var(--text-light)" />
                  <select
                    className="filter-select"
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                  >
                    <option value="">All Projects</option>
                    {projects.map(p => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  className="create-task-btn"
                  onClick={openCreateModal}
                >
                  <FiPlus />
                  Create Task
                </button>
                {isAdminOrManager && (
                  <DroppableButton
                    id="trash"
                    className={`kanban-action-btn trash-btn ${showTrash ? 'active-tab-btn' : ''}`}
                    onClick={() => { setShowTrash(!showTrash); setShowInReview(false); }}
                    title="Trash"
                  >
                    <FiTrash2 size={16} />
                    Trash
                  </DroppableButton>
                )}
                <DroppableButton
                  id="review"
                  className={`kanban-action-btn review-action-btn ${showInReview ? 'active-tab-btn' : ''}`}
                  onClick={() => { setShowInReview(!showInReview); setShowTrash(false); }}
                  title="In Review"
                >
                  <FiCheckCircle size={16} />
                  In Review
                </DroppableButton>
              </div>
            </div>
          )}

        </div>

        {showTrash ? (
          <TrashTab onBack={() => setShowTrash(false)} />
        ) : showInReview ? (
          <InReviewTab onBack={() => setShowInReview(false)} onOpenTask={openFullModal} />
        ) : (
          <div className="kanban-columns">

            {columns.map(column => {

              // ✅ SORT HERE
              const columnTasks =
                getSortedColumnTasks(column.id);

              const ids =
                columnTasks.map(
                  task => task._id
                );

              return (

                <DroppableColumn
                  key={column.id}
                  column={column}
                  taskCount={
                    columnTasks.length
                  }
                >

                  <SortableContext
                    items={ids}
                    strategy={
                      verticalListSortingStrategy
                    }
                  >

                    {columnTasks.map(task => (

                      <TaskCard
                        key={task._id}
                        task={task}
                        onEdit={openEditModal}
                        onOpen={openFullModal}
                      />

                    ))}

                  </SortableContext>

                </DroppableColumn>

              );

            })}

          </div>
        )}


        <DragOverlay>

          {activeId && (

            <TaskCard
              task={
                tasks.find(
                  t => t._id === activeId
                )
              }
              isDragging
            />

          )}

        </DragOverlay>

      </DndContext>


      <TaskModal
        isOpen={isModalOpen}
        onClose={closeModal}
        task={selectedTask}
      />

      <FullTaskModal
        isOpen={isFullModalOpen}
        onClose={closeFullModal}
        task={selectedTaskFull}
      />

    </div>

  );

};


export default KanbanBoard;